import React, { useState } from 'react';
import axios from 'axios';
import Icon from '@avtechsoftware/react-component-library/src/components/common/Icon/Icon';
import qs from 'qs';
import PropTypes from 'prop-types';
import AltErrorWrapper
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/AltErrorWrapper/AltErrorWrapper';
import FormErrorHandler
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/FormErrorHandler';
import FormAlertBox
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/FormAlertBox/FormAlertBox';
import formatFinancial from '../../../utility/formatFinancial';

function AddFundsForm({ setIsFundsFormOpen, creditCardOnFile, balance }) {
  const months = [...Array(12).keys()]
    .map((key) => ({
      name: `${(`0${(key + 1).toString()}`).slice(-2)} - ${new Date(0, key).toLocaleString('en', { month: 'short' })}`,
      value: `${(`0${(key + 1).toString()}`).slice(-2)}`,
    }));
  const year = (new Date()).getFullYear();
  const years = Array.from(new Array(20), (val, index) => index + year);
  const yearOptions = years.map((yr) => ({
    name: yr,
    value: yr.toString().slice(-2),
  }));
  const [isAddingNewCreditCard, setIsAddingNewCreditCard] = useState(false);
  const [creditCardDetails, setCreditCardDetails] = useState({
    cardNumber: '',
    cardExpirationYear: year.toString().slice(-2),
    cardExpirationMonth: '01',
    cardCvv: '',
  });
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState('');
  const [keepCardOnFile, setKeepCardOnFile] = useState(false);
  const [amount, setAmount] = useState(5);
  const [isCustomAmount, setIsCustomAmount] = useState(false);
  const [formErrors, setFormErrors] = useState([]);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [currentFormState, setCurrentFormState] = useState(1);
  const [tax, setTax] = useState(0);

  const handleCustomAmount = (e) => {
    const { value } = e.target;
    const validated = value.toString().match(/^(\d*\.{0,1}\d{0,2}$)/);
    if (validated) {
      setAmount(Number(value));
    }
  };

  const handleNonCustomAmountButton = (e) => {
    const { value } = e.target;
    setFormErrors([]);
    setAmount(Number(value));
    setIsCustomAmount(false);
  };

  const getCalculatedTax = async () => {
    let taxResponse = 0;
    const formData = qs.stringify({
      amount,
    });
    try {
      taxResponse = await axios.post('/sms/calculate-sms-funds-purchase-tax', formData);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('error getting tax for adding sms funds: ', e);
    }
    return taxResponse.data;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormErrors([]);
    setIsFormSubmitting(true);
    if (amount > 5000 || amount < 5) {
      setFormErrors([{ field: 'amount', message: 'Amount must be between $5 and $5000.' }]);
      return;
    }
    const formData = qs.stringify({
      paymentMethod: isAddingNewCreditCard ? 'credit-card-new' : 'credit-card-existing',
      ...(isAddingNewCreditCard && {
        cardNumber: creditCardDetails.cardNumber,
        cardExpirationYear: creditCardDetails.cardExpirationYear,
        cardExpirationMonth: creditCardDetails.cardExpirationMonth,
        cardCvv: creditCardDetails.cardCvv,
      }),
      purchaseOrderNumber,
      keepCardOnFile,
      amount,
      tax,
    });
    const config = {
      headers: {
        'content-type': 'application/x-www-form-urlencoded;charset=utf-8',
      },
    };
    axios.post('/sms/add-sms-funds', formData, config)
      .then((r) => {
        const { success } = r.data;
        if (!success) {
          setFormErrors(r.data.data);
        } else {
          setIsFundsFormOpen(false);
          window.location.reload();
        }
        setIsFormSubmitting(false);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log(err);
        setIsFormSubmitting(false);
      });
  };

  const handleNextStep = async () => {
    setIsFormSubmitting(true);
    const calculatedTax = await getCalculatedTax();
    setIsFormSubmitting(false);
    if (calculatedTax !== undefined) {
      setTax(calculatedTax);
      setCurrentFormState(2);
    } else {
      setFormErrors([{ field: '', message: 'Unexpected Error calculating tax' }]);
    }
  };

  return (
    <div className="container">
      <div className="clearfix text-right my-3">
        <h5 className="float-left">Add funds to your SMS balance</h5>
      </div>
      <FormErrorHandler errorList={formErrors}>
        <FormAlertBox />
        <form>
          {currentFormState === 1
            ? (
              <>
                <div className="form-group row mb-3">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <div className="col-sm-3 font-weight-bold text-md-right">
                    Amount
                  </div>
                  <div className="col-sm-8">
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        checked={amount === 5 && !isCustomAmount}
                        value={5}
                        onChange={handleNonCustomAmountButton}
                      />
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="form-check-label">$5</label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        checked={amount === 25 && !isCustomAmount}
                        value={25}
                        onChange={handleNonCustomAmountButton}
                      />
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="form-check-label">$25</label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        checked={amount === 50 && !isCustomAmount}
                        value={50}
                        onChange={handleNonCustomAmountButton}
                      />
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="form-check-label">$50</label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        checked={isCustomAmount}
                        onChange={() => setIsCustomAmount(true)}
                      />
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="form-check-label mr-1">
                        Other:
                      </label>
                      {
                          isCustomAmount && (
                          <>
                            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                            <label className="form-check-label">$</label>
                            <AltErrorWrapper fieldId="amount">
                              <input
                                type="number"
                                className="ml-1
                   form-control-sm w-auto"
                                onChange={handleCustomAmount}
                                min="5"
                                max="5000"
                                step="0.01"
                                value={amount}
                              />
                            </AltErrorWrapper>
                          </>
                          )
                      }
                    </div>
                  </div>
                </div>
                <div className="form-group row mb-3">
                  <div className="col-sm-3 font-weight-bold text-md-right">
                    Order Summary
                  </div>
                  <div className="col-sm-8">
                    <table className="table table-sm">
                      <tbody>
                        <tr>
                          <th scope="row">SMS Funds</th>
                          <td>
                            $
                            {formatFinancial(amount)}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <span className="text-muted">
                              <i>
                                {`This will bring your balance to $${formatFinancial(amount + Number(balance))}.`}
                              </i>
                            </span>
                          </td>
                          <td />
                        </tr>
                        <tr>
                          <th scope="row">Tax</th>
                          <td>
                            <span className="text-muted">
                              <i>
                                To be calculated at next step.
                              </i>
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">Total Due</th>
                          <td>
                            $
                            {formatFinancial(amount + tax)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )
            : (
              <>
                <div className="form-group row mb-3">
                  <div className="col-sm-3 font-weight-bold text-md-right">
                    Order Summary
                  </div>
                  <div className="col-sm-8">
                    <table className="table table-sm">
                      <tbody>
                        <tr>
                          <th scope="row">SMS Funds</th>
                          <td>
                            $
                            {formatFinancial(amount)}
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <span className="text-muted">
                              <i>
                                {`This will bring your balance to $${formatFinancial(amount + Number(balance))}.`}
                              </i>
                            </span>
                          </td>
                          <td />
                        </tr>
                        <tr>
                          <th scope="row">Tax</th>
                          <td>
                            $
                            {formatFinancial(tax)}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">Total Due</th>
                          <td>
                            $
                            {formatFinancial(amount + tax)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <hr />

                <div className="form-group row">
                  <div className="col-sm-3 font-weight-bold text-md-right">
                    Payment Method
                  </div>
                  <div className="col-sm-8">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        checked={!isAddingNewCreditCard}
                        onChange={() => setIsAddingNewCreditCard(false)}
                        disabled={creditCardOnFile === null}
                      />
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="form-check-label">
                        Credit Card on File -
                        {' '}
                        <Icon icon="credit-card" />
                        {' '}
                        {creditCardOnFile !== null
                          ? <span className="text-muted">{`xxxx${creditCardOnFile.number.slice(-4)} Exp ${creditCardOnFile.expirationDate}`}</span>
                          : null}
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        checked={isAddingNewCreditCard}
                        onChange={() => setIsAddingNewCreditCard(true)}
                      />
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="form-check-label">
                        New Credit Card -
                        {' '}
                        <Icon icon="credit-card" />
                      </label>
                    </div>
                  </div>
                </div>

                <hr />

                {
            isAddingNewCreditCard && (
            <>
              <div className="form-group row">
                <div className="col-sm-3 font-weight-bold text-md-right">
                  Card Number
                </div>
                <div className="col-sm-8">
                  <AltErrorWrapper fieldId="cardNumber">
                    <input
                      name="cardNumber"
                      className="form-control"
                      type="text"
                      onChange={(e) => setCreditCardDetails({
                        ...creditCardDetails, cardNumber: e.target.value,
                      })}
                      value={creditCardDetails.cardNumber}
                    />
                  </AltErrorWrapper>
                </div>
              </div>

              <div className="form-group row">
                <div className="col-sm-3 font-weight-bold text-md-right">
                  Expiration Date
                </div>
                <div className="col-sm-8">
                  {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                  <div className="form-inline">
                    <select
                      className="form-control w-auto mr-3"
                      value={creditCardDetails.cardExpirationMonth}
                      onChange={(e) => setCreditCardDetails({
                        ...creditCardDetails,
                        cardExpirationMonth: e.target.value,
                      })}
                    >
                      {
            months.map((month, index) => <option key={`month${index}`} value={month.value}>{month.name}</option>)
          }
                    </select>
                    <select
                      className="form-control form-control-inline w-auto"
                      value={creditCardDetails.cardExpirationYear}
                      onChange={(e) => setCreditCardDetails({
                        ...creditCardDetails,
                        cardExpirationYear: e.target.value,
                      })}
                    >
                      {
            yearOptions.map((yr, index) => <option key={`year${index}`} value={yr.value}>{yr.name}</option>)
          }
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-group row">
                <div className="col-sm-3 font-weight-bold text-md-right">
                  CVV
                </div>
                <div className="col-sm-8">
                  <AltErrorWrapper fieldId="cardCvv">
                    <input
                      name="cardCvv"
                      className="form-control w-50"
                      type="text"
                      onChange={(e) => setCreditCardDetails({
                        ...creditCardDetails, cardCvv: e.target.value,
                      })}
                      value={creditCardDetails.cardCvv}
                    />
                  </AltErrorWrapper>
                </div>
              </div>

              <div className="form-group row">
                <div className="offset-sm-3 col-sm-8">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      onChange={(e) => setKeepCardOnFile(e.target.checked)}
                      checked={keepCardOnFile}
                    />
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="form-check-label">Replace card on file</label>
                  </div>
                </div>
              </div>
            </>
            )
          }
                <div className="form-group row">
                  <div className="col-sm-3 font-weight-bold text-md-right">
                    Purchase Order #
                    <br />
                    <span className="text-muted font-weight-normal" style={{ fontSize: 'small' }}>(optional)</span>
                  </div>
                  <div className="col-sm-8">
                    <AltErrorWrapper fieldId="purchaseOrderNumber">
                      <input
                        name="purchaseOrderNumber"
                        className="form-control"
                        type="text"
                        onChange={(e) => setPurchaseOrderNumber(e.target.value)}
                        value={purchaseOrderNumber}
                      />
                    </AltErrorWrapper>
                  </div>
                </div>
              </>
            )}
          <hr />
          <div className="d-flex row justify-content-center">
            <div>
              <button
                type="submit"
                className="btn btn-primary"
                onClick={currentFormState === 1 ? handleNextStep : handleSubmit}
                disabled={isFormSubmitting}
              >
                {currentFormState === 1 ? 'Next...' : 'Purchase Now'}
              </button>
              <button
                type="button"
                className="btn btn-link text-muted"
                onClick={() => setIsFundsFormOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </FormErrorHandler>
    </div>
  );
}

AddFundsForm.defaultProps = {
  creditCardOnFile: null,
};

AddFundsForm.propTypes = {
  setIsFundsFormOpen: PropTypes.func.isRequired,
  creditCardOnFile: PropTypes.shape(
    {
      expirationDate: PropTypes.string,
      id: PropTypes.string,
      number: PropTypes.string,
    },
  ),
  balance: PropTypes.string.isRequired,
};

export default AddFundsForm;
